import {Product} from '../classes/product';
import {ProductDetail} from '../classes/product.detail';
import {ShoppingCartLine} from '../classes/shoppingcartline';

/**
 * Utility class for calculating minimum, maximum and available product quantities.
 */
export class ProductQuantityUtils {

	/**
	 * Calculate the available box quantity based on product information and possible shopping cart information.
	 * Box quantity is calculated by dividing the available quantity by the qtyByBox.
	 *
	 * @param product
	 * @param shoppingCartLines
	 */
	public static getAvailableBoxQuantity(product: Product | ProductDetail, shoppingCartLines: ShoppingCartLine[]) {
		const availableQuantity = this.getAvailableQuantity(product, shoppingCartLines);
		return availableQuantity > 0 ? Math.floor(availableQuantity / product.qtyByBox) : 0;
	}

	/**
	 * Gets the minimum order quantity based on the items per ordered unit (tray/layer/trolley)
	 *
	 * @param product
	 * @param itemsPerUnit
	 * @returns {number}
	 */
	public static getMinOrderQuantity(product: Product, itemsPerUnit: number): number {
		// Make sure itemsPerUnit is a number by casting it
		if (+itemsPerUnit === 0) {
			return 1;
		}

		let minimum = product.minimumOrderQuantity / itemsPerUnit;

		if (minimum < 1) {
			minimum = 1;
		}

		return Math.ceil(minimum);
	}

	/**
	 * Calculates the maximum order quantity for a product based on its
	 * package size, available quantity and quantity already ordered in the shopping cart.
	 *
	 * @param packageQuantity amount of products in the package. (based on unit selection such as box, layer or trolley).
	 * @param product product to calculate the maximum order quantity for.
	 * @param shoppingCartAmount already ordered amount.
	 * @return the maximum order quantity for the product, or null for infinite amount.
	 */
	public static getMaxOrderQuantity(packageQuantity: number,
	                                  product: Product,
	                                  shoppingCartAmount: number): number | null {

		if (product.isStockProduct && !product.canOrderInfiniteAmount) {
			return this.calculateMaxOrderQuantity(packageQuantity, product, shoppingCartAmount);
		}

		return null;
	}

	/**
	 * Get the quantity of a product in the shopping cart.
	 *
	 * @param product The product to get the quantity for.
	 * @param shoppingCartLines The shopping cart lines to check for the product.
	 */
	public static getQuantityInShoppingCart(product: Product, shoppingCartLines: ShoppingCartLine[]): number {
		if (!shoppingCartLines) {
			return 0;
		}

		const activeLine = shoppingCartLines.find(line =>
			line.priceListLineNo === product.priceListLineNo &&
			line.priceListNo === product.priceListNo);

		if (activeLine !== undefined) {
			return activeLine.quantity;
		}

		return 0;
	}

	private static calculateMaxOrderQuantity(packageQuantity: number,
	                                         product: Product,
	                                         shoppingCartAmount: number): number | null {

		const availableAmount = product.availableQuantity;

		const remainingAmount = availableAmount - shoppingCartAmount;

		const remainingPackageAmount = Math.floor(remainingAmount / packageQuantity);

		return remainingPackageAmount > 0 ? remainingPackageAmount : 0;
	}

	/**
	 * Calculate the available quantity based on product information and possible shopping cart information.
	 *
	 * @param product
	 * @param shoppingCartLines
	 */
	private static getAvailableQuantity(product: Product | ProductDetail, shoppingCartLines: ShoppingCartLine[]): number {
		if (!shoppingCartLines) {
			return product.availableQuantity;
		}

		const quantityInShoppingCart = this.getQuantityInShoppingCart(product, shoppingCartLines);

		const available = product.availableQuantity - quantityInShoppingCart;

		return available < 0 ? 0 : available;
	}
}
